import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send, Plus, Loader2, ChevronLeft, Mail, Clock, Sparkles, Wand2,
  Play, Pause, Trash2, Users, Eye, MousePointerClick, Reply,
  GripVertical, ArrowLeft, ChevronRight, X, Check, Activity, HelpCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, errorMessage } from "@/lib/api";
import { toast } from "sonner";
import { ModuleGuide } from "@/components/ModuleGuide";
import { SEQUENCES_GUIDE } from "@/lib/guides";
import { SamTipCard } from "@/components/SamTipCard";
import { useSamTip } from "@/context/SamTipsContext";

const STATUS_PILL = {
  draft:  { label: "Draft",  cls: "border-white/15 bg-white/5 text-white/65" },
  active: { label: "Active", cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" },
  paused: { label: "Paused", cls: "border-amber-500/30 bg-amber-500/10 text-amber-300" },
};

function StatusPill({ status }) {
  const c = STATUS_PILL[status] || STATUS_PILL.draft;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-wider ${c.cls}`}>
      {c.label}
    </span>
  );
}

function formatPct(n) {
  return `${Math.round((n || 0) * 1000) / 10}%`;
}

// ============ LIST PAGE ============

export default function Sequences() {
  const navigate = useNavigate();
  const tip = useSamTip("sequences");
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchSequences = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/sequences");
      setSequences(r.data);
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSequences(); }, [fetchSequences]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/sequences/${deleteTarget.id}`);
      toast.success("Sequence deleted");
      setDeleteTarget(null);
      fetchSequences();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <div className="mx-auto max-w-7xl">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-eyebrow text-emerald-400" data-testid="sequences-eyebrow">Outreach sequences</div>
            <h1 className="font-display mt-2 text-4xl font-light tracking-tight sm:text-5xl">
              Cadences that <span className="brand-text">close</span>.
            </h1>
            <p className="mt-2 max-w-xl text-white/55">
              Multi-step email sequences with AI-written copy, smart delays, and real-time
              engagement analytics. Stop-on-reply built in.
            </p>
          </div>
          <Button
            onClick={() => setCreateOpen(true)}
            className="rounded-xl bg-white text-black hover:bg-zinc-200"
            data-testid="sequences-new-btn"
          >
            <Plus className="mr-1.5 h-4 w-4" /> New sequence
          </Button>
        </div>
      </motion.div>

      <div className="mt-8">
        <SamTipCard
          show={!loading && sequences.length === 0}
          enabled={tip.enabled}
          message={tip.message}
          prefill={tip.prefill}
          testId="sam-tip-sequences"
          module="sequences"
        />
        {loading ? (
          <div className="flex items-center justify-center py-20 text-white/45">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : sequences.length === 0 ? (
          <EmptyState onCreate={() => setCreateOpen(true)} />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="sequences-grid">
            {sequences.map((s, i) => (
              <SequenceCard
                key={s.id}
                seq={s}
                index={i}
                onOpen={() => navigate(`/app/sequences/${s.id}`)}
                onDelete={() => setDeleteTarget(s)}
              />
            ))}
          </div>
        )}
      </div>

      <CreateSequenceDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(seq) => { setCreateOpen(false); navigate(`/app/sequences/${seq.id}`); }}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="border-white/10 bg-[#0a0a0a] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/55">
              This deletes the sequence, all steps, and all enrollments. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/15 bg-transparent text-white hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 text-white hover:bg-red-600" data-testid="sequence-delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SequenceCard({ seq, index, onOpen, onDelete }) {
  const sent = seq.stats?.sent || 0;
  const opened = seq.stats?.opened || 0;
  const replied = seq.stats?.replied || 0;
  const openRate = sent ? opened / sent : 0;
  const replyRate = sent ? replied / sent : 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="glass glass-hover group relative overflow-hidden rounded-2xl p-5"
      data-testid={`sequence-card-${seq.id}`}
    >
      <button onClick={onOpen} className="block w-full text-left">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="truncate font-display text-xl font-light text-white" title={seq.name}>{seq.name}</div>
            <div className="mt-1 line-clamp-2 text-xs text-white/45">{seq.description || "—"}</div>
          </div>
          <StatusPill status={seq.status} />
        </div>
        <div className="mt-5 grid grid-cols-3 gap-3 text-center">
          <div>
            <div className="text-eyebrow text-white/40">Steps</div>
            <div className="font-display mt-1 text-xl text-white">{seq.steps_count}</div>
          </div>
          <div>
            <div className="text-eyebrow text-white/40">Sent</div>
            <div className="font-display mt-1 text-xl text-white">{sent}</div>
          </div>
          <div>
            <div className="text-eyebrow text-white/40">Reply</div>
            <div className="font-display mt-1 text-xl text-fuchsia-300">{formatPct(replyRate)}</div>
          </div>
        </div>
        <div className="mt-4 h-1 overflow-hidden rounded-full bg-white/5">
          <div className="h-full brand-bg" style={{ width: `${Math.min(100, openRate * 100)}%` }} />
        </div>
        <div className="mt-2 flex items-center justify-between text-[10px] uppercase tracking-wider text-white/40">
          <span>Open rate · {formatPct(openRate)}</span>
          <span>{seq.stats?.enrolled || 0} enrolled</span>
        </div>
      </button>
      <button
        onClick={onDelete}
        className="absolute right-3 top-3 rounded-lg p-1.5 text-white/30 opacity-0 transition hover:bg-red-500/10 hover:text-red-300 group-hover:opacity-100"
        data-testid={`sequence-delete-${seq.id}`}
        title="Delete sequence"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </motion.div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="glass rounded-2xl p-12 text-center" data-testid="sequences-empty">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl brand-bg">
        <Send className="h-6 w-6 text-white" />
      </div>
      <h2 className="font-display mt-5 text-2xl font-light">No sequences yet</h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-white/55">
        A sequence is a multi-step cadence that contacts leads with the right message at the right time —
        AI writes the copy, the system handles the timing.
      </p>
      <Button onClick={onCreate} className="mt-6 rounded-full bg-white text-black hover:bg-zinc-200" data-testid="sequences-empty-create">
        <Plus className="mr-1.5 h-4 w-4" /> Create your first sequence
      </Button>
    </div>
  );
}

function CreateSequenceDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!open) { setName(""); setDesc(""); } }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post("/sequences", { name: name.trim(), description: desc.trim() || null });
      toast.success("Sequence created");
      onCreated?.(r.data);
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-[#0a0a0a] text-white sm:max-w-md" data-testid="create-sequence-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">New sequence</DialogTitle>
          <DialogDescription className="text-white/55">
            Give it a name, then build the steps. You can rename it later.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-white/50">Name</Label>
            <Input
              value={name} onChange={(e) => setName(e.target.value)} required
              placeholder="e.g. Q1 ICP cold outreach"
              className="border-white/10 bg-black/40 text-white"
              data-testid="new-sequence-name"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-white/50">Description (optional)</Label>
            <Textarea
              value={desc} onChange={(e) => setDesc(e.target.value)}
              placeholder="What's this cadence for?"
              rows={3}
              className="border-white/10 bg-black/40 text-white"
              data-testid="new-sequence-desc"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-white/65 hover:text-white">
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !name.trim()} className="rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="new-sequence-save">
              {saving && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />} Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============ BUILDER PAGE ============

export function SequenceBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [seq, setSeq] = useState(null);
  const [steps, setSteps] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingMeta, setSavingMeta] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tab, setTab] = useState("steps");

  const loadAll = useCallback(async () => {
    try {
      const [sR, stR, aR, eR, actR] = await Promise.all([
        api.get(`/sequences/${id}`),
        api.get(`/sequences/${id}/steps`),
        api.get(`/sequences/${id}/analytics`).catch(() => ({ data: null })),
        api.get(`/sequences/${id}/enrollments`).catch(() => ({ data: [] })),
        api.get(`/sequences/${id}/activity?limit=50`).catch(() => ({ data: [] })),
      ]);
      setSeq(sR.data);
      setName(sR.data.name);
      setDescription(sR.data.description || "");
      setSteps(stR.data);
      setAnalytics(aR.data);
      setEnrollments(eR.data);
      setActivity(actR.data);
    } catch (err) {
      toast.error(errorMessage(err));
      navigate("/app/sequences");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Auto-refresh activity + analytics every 10s when active
  useEffect(() => {
    if (!seq || seq.status !== "active") return;
    const t = setInterval(async () => {
      try {
        const [aR, actR, sR] = await Promise.all([
          api.get(`/sequences/${id}/analytics`),
          api.get(`/sequences/${id}/activity?limit=50`),
          api.get(`/sequences/${id}`),
        ]);
        setAnalytics(aR.data);
        setActivity(actR.data);
        setSeq(sR.data);
      } catch (_) {}
    }, 10000);
    return () => clearInterval(t);
    // Only re-arm the poller when sequence id or status flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, seq?.status]);

  const saveMeta = async () => {
    setSavingMeta(true);
    try {
      const r = await api.patch(`/sequences/${id}`, { name, description: description || null });
      setSeq(r.data);
      toast.success("Saved");
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setSavingMeta(false); }
  };

  const activate = async () => {
    try {
      const r = await api.post(`/sequences/${id}/activate`);
      setSeq(r.data);
      toast.success("Sequence activated");
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const pause = async () => {
    try {
      const r = await api.post(`/sequences/${id}/pause`);
      setSeq(r.data);
      toast("Sequence paused");
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const addStep = async () => {
    try {
      const r = await api.post(`/sequences/${id}/steps`, {
        type: "email", channel: "email",
        subject: "", body: "",
        delay_amount: steps.length === 0 ? 0 : 2,
        delay_unit: "days",
      });
      setSteps((s) => [...s, r.data]);
      toast.success("Step added");
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const addWait = async () => {
    try {
      const r = await api.post(`/sequences/${id}/steps`, {
        type: "wait", channel: "email",
        delay_amount: 2, delay_unit: "days",
      });
      setSteps((s) => [...s, r.data]);
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const patchStep = async (stepId, patch) => {
    setSteps((arr) => arr.map((s) => (s.id === stepId ? { ...s, ...patch } : s)));
    try {
      await api.patch(`/sequences/${id}/steps/${stepId}`, patch);
    } catch (err) { toast.error(errorMessage(err)); loadAll(); }
  };

  const deleteStep = async (stepId) => {
    try {
      await api.delete(`/sequences/${id}/steps/${stepId}`);
      setSteps((arr) => arr.filter((s) => s.id !== stepId));
      toast.success("Step removed");
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const moveStep = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= steps.length) return;
    const next = [...steps];
    [next[idx], next[target]] = [next[target], next[idx]];
    setSteps(next);
    try {
      await api.post(`/sequences/${id}/steps/reorder`, { ordered_step_ids: next.map((s) => s.id) });
    } catch (err) { toast.error(errorMessage(err)); loadAll(); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-white/45">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (!seq) return null;

  return (
    <div className="mx-auto max-w-6xl">
      <Link
        to="/app/sequences"
        className="inline-flex items-center gap-1 text-sm text-white/55 hover:text-white"
        data-testid="builder-back"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> All sequences
      </Link>

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <Input
                value={name} onChange={(e) => setName(e.target.value)} onBlur={saveMeta}
                className="font-display border-white/10 bg-transparent text-3xl font-light tracking-tight text-white sm:text-4xl"
                data-testid="builder-name-input"
              />
              <StatusPill status={seq.status} />
            </div>
            <Input
              value={description} onChange={(e) => setDescription(e.target.value)} onBlur={saveMeta}
              placeholder="Add a short description"
              className="border-white/10 bg-transparent text-sm text-white/65"
              data-testid="builder-description-input"
            />
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            {seq.status === "active" ? (
              <Button onClick={pause} variant="outline" className="rounded-xl border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid="builder-pause-btn">
                <Pause className="mr-1.5 h-4 w-4" /> Pause
              </Button>
            ) : (
              <Button onClick={activate} disabled={steps.length === 0} className="rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="builder-activate-btn">
                <Play className="mr-1.5 h-4 w-4" /> Activate
              </Button>
            )}
          </div>
        </div>
      </motion.div>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="bg-white/5 text-white" data-testid="builder-tabs">
          <TabsTrigger value="steps" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="tab-steps">
            Steps · {steps.length}
          </TabsTrigger>
          <TabsTrigger value="analytics" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="tab-analytics">
            Analytics
          </TabsTrigger>
          <TabsTrigger value="enrollments" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="tab-enrollments">
            Enrollments · {enrollments.length}
          </TabsTrigger>
          <TabsTrigger value="activity" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="tab-activity">
            Activity
          </TabsTrigger>
          <TabsTrigger value="guide" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="tab-guide">
            <HelpCircle className="mr-1 h-3.5 w-3.5" /> Guide
          </TabsTrigger>
        </TabsList>

        <TabsContent value="steps" className="mt-5">
          <div className="space-y-3" data-testid="steps-list">
            <AnimatePresence>
              {steps.map((s, idx) => (
                <StepCard
                  key={s.id}
                  step={s}
                  index={idx}
                  total={steps.length}
                  sequenceId={id}
                  onPatch={(p) => patchStep(s.id, p)}
                  onDelete={() => deleteStep(s.id)}
                  onMoveUp={() => moveStep(idx, -1)}
                  onMoveDown={() => moveStep(idx, +1)}
                />
              ))}
            </AnimatePresence>

            <div className="flex flex-wrap gap-2 pt-2">
              <Button onClick={addStep} className="rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="add-email-step">
                <Mail className="mr-1.5 h-4 w-4" /> Add email step
              </Button>
              <Button onClick={addWait} variant="outline" className="rounded-xl border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid="add-wait-step">
                <Clock className="mr-1.5 h-4 w-4" /> Add wait
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="mt-5">
          <AnalyticsPane analytics={analytics} seq={seq} />
        </TabsContent>

        <TabsContent value="enrollments" className="mt-5">
          <EnrollmentsPane enrollments={enrollments} />
        </TabsContent>

        <TabsContent value="activity" className="mt-5">
          <ActivityPane activity={activity} />
        </TabsContent>

        <TabsContent value="guide" className="mt-5">
          <ModuleGuide
            title={SEQUENCES_GUIDE.title}
            whatItIs={SEQUENCES_GUIDE.whatItIs}
            capabilities={SEQUENCES_GUIDE.capabilities}
            howTo={SEQUENCES_GUIDE.howTo}
            ctaLabel={SEQUENCES_GUIDE.cta}
            onCta={() => setTab("steps")}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StepCard({ step, index, total, sequenceId, onPatch, onDelete, onMoveUp, onMoveDown }) {
  const [aiOpen, setAiOpen] = useState(false);
  const isWait = step.type === "wait";

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.25 }}
      className="glass rounded-2xl p-5"
      data-testid={`step-card-${step.id}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white/70">
            {isWait ? <Clock className="h-4 w-4" /> : <Mail className="h-4 w-4 text-fuchsia-300" />}
          </div>
          <div>
            <div className="text-eyebrow text-white/40">Step {index + 1} of {total}</div>
            <div className="font-display text-lg font-light text-white">
              {isWait ? "Wait" : "Email"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onMoveUp} disabled={index === 0}
            className="rounded-lg p-1.5 text-white/40 hover:bg-white/5 hover:text-white disabled:opacity-30"
            data-testid={`step-up-${step.id}`}
            title="Move up"
          >↑</button>
          <button
            onClick={onMoveDown} disabled={index === total - 1}
            className="rounded-lg p-1.5 text-white/40 hover:bg-white/5 hover:text-white disabled:opacity-30"
            data-testid={`step-down-${step.id}`}
            title="Move down"
          >↓</button>
          <button
            onClick={onDelete}
            className="rounded-lg p-1.5 text-white/40 hover:bg-red-500/10 hover:text-red-300"
            data-testid={`step-delete-${step.id}`}
            title="Delete step"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-white/50">Delay before this step</Label>
          <div className="flex gap-2">
            <Input
              type="number" min="0" value={step.delay_amount}
              onChange={(e) => onPatch({ delay_amount: parseInt(e.target.value || "0", 10) })}
              className="w-24 border-white/10 bg-black/40 text-white"
              data-testid={`step-delay-amount-${step.id}`}
            />
            <Select value={step.delay_unit} onValueChange={(v) => onPatch({ delay_unit: v })}>
              <SelectTrigger className="border-white/10 bg-black/40 text-white" data-testid={`step-delay-unit-${step.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-white/10 bg-[#0a0a0a] text-white">
                <SelectItem value="minutes">minutes</SelectItem>
                <SelectItem value="hours">hours</SelectItem>
                <SelectItem value="days">days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {!isWait && (
          <>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-white/50">Channel</Label>
              <Select value={step.channel} onValueChange={(v) => onPatch({ channel: v })}>
                <SelectTrigger className="border-white/10 bg-black/40 text-white" data-testid={`step-channel-${step.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-[#0a0a0a] text-white">
                  <SelectItem value="email">Email (Gmail mock)</SelectItem>
                  <SelectItem value="linkedin">LinkedIn (coming soon)</SelectItem>
                  <SelectItem value="sms">SMS (coming soon)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}
      </div>

      {!isWait && (
        <>
          <div className="mt-4 space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wider text-white/50">Subject</Label>
              <Button
                size="sm" variant="ghost"
                onClick={() => setAiOpen(true)}
                className="h-7 rounded-lg text-fuchsia-300 hover:bg-fuchsia-500/10 hover:text-fuchsia-200"
                data-testid={`step-ai-copy-${step.id}`}
              >
                <Wand2 className="mr-1 h-3 w-3" /> AI write
              </Button>
            </div>
            <Input
              value={step.subject || ""}
              onChange={(e) => onPatch({ subject: e.target.value })}
              placeholder="Quick thought, {{first_name}}"
              className="border-white/10 bg-black/40 text-white"
              data-testid={`step-subject-${step.id}`}
            />
          </div>
          <div className="mt-3 space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-white/50">Body</Label>
            <Textarea
              value={step.body || ""}
              onChange={(e) => onPatch({ body: e.target.value })}
              rows={6}
              placeholder={`Hi {{first_name}},\n\nYour email body here. You can use merge tags like {{company}}, {{title}}, {{personalization}}.`}
              className="border-white/10 bg-black/40 text-white font-mono text-sm"
              data-testid={`step-body-${step.id}`}
            />
            <div className="text-[10px] text-white/40">
              Merge tags: <code className="text-white/65">{`{{first_name}}`}</code>,{" "}
              <code className="text-white/65">{`{{company}}`}</code>,{" "}
              <code className="text-white/65">{`{{title}}`}</code>,{" "}
              <code className="text-white/65">{`{{personalization}}`}</code>
            </div>
          </div>

          <MergeTagPreview stepId={step.id} subject={step.subject} body={step.body} />
        </>
      )}

      <AICopyDialog
        open={aiOpen} onOpenChange={setAiOpen}
        sequenceId={sequenceId} stepId={step.id}
        onSaved={(subject, body) => { onPatch({ subject, body }); setAiOpen(false); }}
      />
    </motion.div>
  );
}

function MergeTagPreview({ stepId, subject, body }) {
  const [open, setOpen] = useState(false);
  const [leads, setLeads] = useState([]);
  const [leadId, setLeadId] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || leads.length > 0) return;
    setLoading(true);
    api.get("/leads?page=1&page_size=8")
      .then((r) => {
        const items = r.data?.items || [];
        setLeads(items);
        if (items.length && !leadId) setLeadId(items[0].id);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selected = leads.find((l) => l.id === leadId);
  const render = (template) => {
    if (!template) return "";
    const first = (selected?.name || "").split(" ")[0] || "there";
    const map = {
      first_name: first,
      name: selected?.name || "there",
      company: selected?.company || "your company",
      title: selected?.title || "",
      personalization: selected?.personalization || "Saw your recent post on AI — sharp take.",
      email: selected?.email || "",
    };
    return template.replace(/\{\{\s*([a-zA-Z_]+)\s*\}\}/g, (_, key) => {
      if (key in map) return map[key];
      // Custom fields fallback
      const cf = (selected?.custom_fields || {})[key];
      return cf != null ? String(cf) : `{{${key}}}`;
    });
  };

  const hasMergeTag = /\{\{[a-zA-Z_]+\}\}/.test(`${subject || ""} ${body || ""}`);

  return (
    <div className="mt-3 rounded-xl border border-white/5 bg-white/[0.02]" data-testid={`step-preview-${stepId}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-3 py-2 text-left"
        data-testid={`step-preview-toggle-${stepId}`}
      >
        <span className="flex items-center gap-2 text-xs">
          <Eye className="h-3.5 w-3.5 text-fuchsia-300" />
          <span className="font-medium text-white/85">Live preview</span>
          {hasMergeTag ? (
            <span className="rounded-full border border-fuchsia-500/30 bg-fuchsia-500/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-fuchsia-300">
              Merge tags detected
            </span>
          ) : (
            <span className="text-white/40">— preview compiled for a real lead</span>
          )}
        </span>
        <ChevronRight className={`h-3.5 w-3.5 text-white/45 transition ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-white/5 p-3 text-sm" data-testid={`step-preview-pane-${stepId}`}>
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-white/45">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading leads…
            </div>
          ) : leads.length === 0 ? (
            <div className="text-xs text-white/55">
              Add or import leads in Smart Leads to preview compiled merge tags.
            </div>
          ) : (
            <>
              <div className="mb-3 flex items-center gap-2">
                <Label className="text-[10px] uppercase tracking-wider text-white/40">Preview as</Label>
                <Select value={leadId} onValueChange={setLeadId}>
                  <SelectTrigger className="h-8 max-w-xs border-white/10 bg-black/40 text-white" data-testid={`step-preview-lead-${stepId}`}>
                    <SelectValue placeholder="Select lead" />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-[#0a0a0a] text-white">
                    {leads.map((l) => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.name}{l.company ? ` · ${l.company}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 rounded-lg border border-white/5 bg-black/40 p-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-white/40">Subject</div>
                  <div className="mt-0.5 text-white" data-testid={`step-preview-subject-${stepId}`}>
                    {render(subject) || <span className="italic text-white/40">(empty)</span>}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-white/40">Body</div>
                  <div className="mt-0.5 whitespace-pre-wrap text-white/85" data-testid={`step-preview-body-${stepId}`}>
                    {render(body) || <span className="italic text-white/40">(empty)</span>}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function AICopyDialog({ open, onOpenChange, sequenceId, stepId, onSaved }) {
  const [prompt, setPrompt] = useState("");
  const [tone, setTone] = useState("direct and friendly");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!open) { setPrompt(""); setPreview(null); }
  }, [open]);

  const run = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    setPreview(null);
    try {
      const r = await api.post(`/sequences/${sequenceId}/ai-copy`, {
        prompt: prompt.trim(), tone, step_id: null,
      });
      setPreview(r.data);
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setLoading(false); }
  };

  const apply = () => {
    if (!preview) return;
    onSaved(preview.subject, preview.body);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-[#0a0a0a] text-white sm:max-w-xl" data-testid="ai-copy-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-2xl">
            <Sparkles className="h-5 w-5 text-fuchsia-400" /> AI write this step
          </DialogTitle>
          <DialogDescription className="text-white/55">
            Describe what to say. The AI writes a subject + body using merge tags.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-white/50">What do you want to say?</Label>
            <Textarea
              value={prompt} onChange={(e) => setPrompt(e.target.value)}
              rows={4}
              placeholder="Intro email pitching Acme's outbound service to a VP of Sales at a series-B SaaS — under 90 words."
              className="border-white/10 bg-black/40 text-white"
              data-testid="ai-copy-prompt"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-white/50">Tone</Label>
            <Input
              value={tone} onChange={(e) => setTone(e.target.value)}
              className="border-white/10 bg-black/40 text-white"
              data-testid="ai-copy-tone"
            />
          </div>

          {preview && (
            <div className="space-y-2 rounded-xl border border-fuchsia-500/20 bg-fuchsia-500/5 p-3 text-sm text-white/80" data-testid="ai-copy-preview">
              <div>
                <div className="text-eyebrow text-fuchsia-300">Subject</div>
                <div className="mt-1 text-white">{preview.subject}</div>
              </div>
              <div>
                <div className="text-eyebrow text-fuchsia-300">Body</div>
                <div className="mt-1 whitespace-pre-wrap text-white/85">{preview.body}</div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-white/65 hover:text-white">Cancel</Button>
          {!preview ? (
            <Button onClick={run} disabled={loading || !prompt.trim()} className="rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="ai-copy-generate">
              {loading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Wand2 className="mr-1 h-3.5 w-3.5" />} Generate
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => setPreview(null)} className="border-white/15 bg-white/5 text-white hover:bg-white/10">
                Regenerate
              </Button>
              <Button onClick={apply} className="rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="ai-copy-apply">
                <Check className="mr-1 h-3.5 w-3.5" /> Apply to step
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AnalyticsPane({ analytics, seq }) {
  if (!analytics) return <div className="text-sm text-white/45">No analytics yet.</div>;
  const cards = [
    { label: "Enrolled",  value: analytics.enrolled, icon: Users,             accent: "from-indigo-500/40 to-indigo-500/10 border-indigo-500/20" },
    { label: "Sent",      value: analytics.sent,     icon: Mail,              accent: "from-violet-500/40 to-violet-500/10 border-violet-500/20" },
    { label: "Opened",    value: analytics.opened,   icon: Eye,               accent: "from-fuchsia-500/40 to-fuchsia-500/10 border-fuchsia-500/20", sub: formatPct(analytics.open_rate) },
    { label: "Replied",   value: analytics.replied,  icon: Reply,             accent: "from-emerald-500/30 to-emerald-500/10 border-emerald-500/20", sub: formatPct(analytics.reply_rate) },
  ];
  return (
    <div className="space-y-6" data-testid="analytics-pane">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="glass rounded-2xl p-5" data-testid={`metric-${c.label.toLowerCase()}`}>
            <div className="flex items-center justify-between">
              <div className="text-eyebrow text-white/40">{c.label}</div>
              <div className={`flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br border ${c.accent}`}>
                <c.icon className="h-4 w-4 text-white/90" />
              </div>
            </div>
            <div className="font-display mt-3 text-4xl font-light tracking-tight">{c.value}</div>
            {c.sub && <div className="mt-1 text-xs text-fuchsia-300">{c.sub}</div>}
          </div>
        ))}
      </div>

      {analytics.steps.length > 0 && (
        <div className="glass rounded-2xl p-5" data-testid="per-step-analytics">
          <div className="text-eyebrow text-white/40">Per-step breakdown</div>
          <div className="mt-4 space-y-3">
            {analytics.steps.map((s) => (
              <div key={s.step_id} className="rounded-xl border border-white/5 bg-white/[0.02] p-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-white">
                    <span className="font-mono text-white/45">#{s.order + 1}</span>{" "}
                    {s.subject ? <span className="ml-1">{s.subject}</span> : <span className="italic text-white/45">{s.type === "wait" ? "Wait step" : "(no subject)"}</span>}
                  </div>
                  <div className="text-xs text-white/55">{s.sent} sent · {s.opened} opened · {s.replied} replied</div>
                </div>
                <div className="mt-2 flex gap-1">
                  <div className="flex-1 h-1 overflow-hidden rounded-full bg-white/5">
                    <div className="h-full brand-bg" style={{ width: `${s.sent ? (s.opened / s.sent) * 100 : 0}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function EnrollmentsPane({ enrollments }) {
  if (enrollments.length === 0) {
    return (
      <div className="glass rounded-2xl p-12 text-center text-white/55" data-testid="enrollments-empty">
        <Users className="mx-auto h-7 w-7 text-white/30" />
        <div className="font-display mt-3 text-xl font-light">No enrollments yet</div>
        <p className="mx-auto mt-2 max-w-md text-sm">
          Head to <Link to="/app/leads" className="text-fuchsia-300 hover:underline">Smart Leads</Link>,
          select leads, and click <span className="text-white">Enroll in sequence</span>.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="enrollments-list">
      {enrollments.map((e) => (
        <div key={e.id} className="glass flex items-center justify-between rounded-xl p-3">
          <div className="min-w-0">
            <div className="truncate text-sm text-white">{e.lead_name || "—"}</div>
            <div className="truncate text-xs text-white/45">{e.lead_email || "—"}</div>
          </div>
          <div className="flex items-center gap-3 text-right">
            <div className="text-xs text-white/55">
              Step {(e.current_step_order ?? 0) + 1}
            </div>
            <StatusPill status={e.status === "active" ? "active" : e.status === "paused" ? "paused" : "draft"} />
          </div>
        </div>
      ))}
    </div>
  );
}

function ActivityPane({ activity }) {
  if (activity.length === 0) {
    return (
      <div className="glass rounded-2xl p-12 text-center text-white/55" data-testid="activity-empty">
        <Activity className="mx-auto h-7 w-7 text-white/30" />
        <div className="font-display mt-3 text-xl font-light">No activity yet</div>
        <p className="mx-auto mt-2 max-w-md text-sm">
          Sends, opens, clicks, and replies will appear here in real time.
        </p>
      </div>
    );
  }
  const iconFor = (s) => {
    if (s === "replied") return Reply;
    if (s === "clicked") return MousePointerClick;
    if (s === "opened") return Eye;
    return Mail;
  };
  const colorFor = (s) => ({
    replied: "text-emerald-300",
    clicked: "text-fuchsia-300",
    opened:  "text-violet-300",
    sent:    "text-white/65",
  }[s] || "text-white/65");

  return (
    <div className="space-y-2" data-testid="activity-list">
      {activity.map((a) => {
        const Icon = iconFor(a.status);
        return (
          <div key={a.id} className="glass flex items-center justify-between rounded-xl p-3">
            <div className="flex items-center gap-3 min-w-0">
              <Icon className={`h-4 w-4 ${colorFor(a.status)}`} />
              <div className="min-w-0">
                <div className="truncate text-sm text-white">{a.subject_rendered || "(no subject)"}</div>
                <div className="truncate text-xs text-white/45">To {a.lead_name || "—"} · step {a.step_order + 1}</div>
              </div>
            </div>
            <div className="text-right text-xs">
              <div className={`capitalize ${colorFor(a.status)}`}>{a.status}</div>
              <div className="text-white/40">{new Date(a.sent_at).toLocaleTimeString()}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
